import { useCallback } from "react";
import { graphql, useMutation } from "react-relay/hooks";

import type { unlinkDeviceFromOrganizationMutation } from "__generated__/unlinkDeviceFromOrganizationMutation.graphql";

/**
 * This hook is used to unlink the device from the organization (and whatever else the `unclaimDevice` mutation does),
 * and *refresh* the Relay state for the device, rather than expunging it entirely. The purpose is to be able to
 * use this hook from the Admin panel within customer portal - the device isn't defunct, it's just no longer tied
 * to the org.
 */

// Note: We're still using the `unclaimDevice` mutation in the backend, but
// it's aliased here for reading the response in the hook.
// The response needs to update a few fields (displayName changes, deviceHealth), to have a proper UI state post-unlink.
const requestUnlinkDeviceFromOrganizationMutation = graphql`
  mutation unlinkDeviceFromOrganizationMutation($deviceNodeId: ID!) {
    unlinkDeviceFromOrganization: unclaimDevice(deviceNodeId: $deviceNodeId) {
      accessPoint {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      attachment {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      battery {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      controller {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      dock {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      sensor {
        id
        displayName
        organization {
          id
        }
      }
      vehicle {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      gimbal {
        id
        displayName
        deviceHealthStatus
        organization {
          id
        }
      }
      errors
    }
  }
`;

export const useUnlinkDeviceFromOrganization = (
  onCompleted?: (response: unlinkDeviceFromOrganizationMutation["response"]) => void
) => {
  const [requestUnlinkDeviceFromOrganization, loading] =
    useMutation<unlinkDeviceFromOrganizationMutation>(requestUnlinkDeviceFromOrganizationMutation);

  const callback = useCallback(
    (deviceNodeId: string) =>
      requestUnlinkDeviceFromOrganization({
        variables: { deviceNodeId },
        onCompleted,
      }),
    [requestUnlinkDeviceFromOrganization, onCompleted]
  );

  return [callback, loading] as [typeof callback, boolean];
};
